import {
  DataGrid,
  GridColDef,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { Box, Button, Grid, Stack, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import IconButton from "@mui/material/IconButton";
import React from "react";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { dataGridRoTextConstants } from "../../../constants/dataGridRoTextConstants";
import DeleteRowDialog from "../../dialogs/DeleteRowDialog";
import { getName } from "../../../functions/valueGetterFunctions/countryCountyCityGetterFunctions";
import { countryRequest } from "../../../api/CountryRequest";
import { useNavigate, useParams } from "react-router-dom";
import AddCityDialog from "../../dialogs/country/AddCityDialog";
import EditCityDialog from "../../dialogs/country/EditCityDialog";

export default function CitiesDataGrid() {
  const { id } = useParams();
  const columns: GridColDef[] = [
    { field: "id", headerName: "ID", width: 90, flex: 1, hide: true },
    {
      field: "name",
      headerName: "Nume",
      width: 150,
      flex: 1,
      valueGetter: getName,
    },
    {
      field: "actions",
      headerName: "Acțiuni",
      minWidth: 90,
      maxWidth: 90,
      filterable: false,
      renderCell: ({ row: city }) => {
        return [
          <Tooltip title="Editează" placement="bottom">
            <IconButton
              onClick={() => {
                handleOpenEditCityDialog(city);
              }}
            >
              <EditIcon fontSize="small" color="warning" />
            </IconButton>
          </Tooltip>,
          <Tooltip title="Șterge" placement="bottom">
            <IconButton onClick={() => handleOpenDeleteRowDialog(city.id)}>
              <DeleteIcon fontSize="small" color="error" />
            </IconButton>
          </Tooltip>,
        ];
      },
    },
  ];

  const [pageSize, setPageSize] = React.useState<number>(10);
  const [rows, setRows] = useState<City[]>([]);

  const fetchCities = () => {
    countryRequest.getCities(Number(id)).then((cities) => setRows(cities));
  };

  useEffect(() => {
    fetchCities();
  }, []);

  const [selectedCityForEdit, setSelectedCityForEdit] = useState<
    City | undefined
  >(undefined);

  const [openAddCityDialog, setOpenAddCityDialog] = React.useState(false);
  const handleOpenAddCityDialog = () => {
    setOpenAddCityDialog(true);
  };

  const [openEditCityDialog, setOpenEditCityDialog] = React.useState(false);
  const handleOpenEditCityDialog = (city: City) => {
    setOpenEditCityDialog(true);
    setSelectedCityForEdit(city);
  };

  const [toBeDeleted, setToBeDeleted] = React.useState<number | null>(null);
  const handleOpenDeleteRowDialog = (id: any) => {
    setToBeDeleted(id);
  };
  const handleCloseDeleteRowDialog = () => {
    setToBeDeleted(null);
  };
  const openDeleteRowDialog = Boolean(toBeDeleted);

  const [filterButtonEl, setFilterButtonEl] = React.useState(null);

  const CustomToolbar = React.useCallback(() => {
    return (
      <GridToolbarContainer>
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
        >
          <Grid
            item
            sx={{
              ml: 1,
            }}
          >
            <GridToolbarQuickFilter />
          </Grid>
          <Grid
            item
            sx={{
              mr: 0.3,
            }}
          >
            <Grid container direction="row" spacing={1}>
              <Grid item>
                <Button size="small" onClick={handleOpenAddCityDialog}>
                  <Stack direction="row">
                    <AddIcon
                      fontSize="small"
                      sx={{
                        mr: 1,
                      }}
                    />
                    Adaugă oraș
                  </Stack>
                </Button>
              </Grid>
              <Grid item>
                {/* @ts-ignore */}
                <GridToolbarColumnsButton ref={setFilterButtonEl} />
              </Grid>
              <Grid item>
                {/* @ts-ignore */}
                <GridToolbarDensitySelector ref={setFilterButtonEl} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </GridToolbarContainer>
    );
  }, []);

  return (
    <Box
      sx={{
        height: 400,
        width: "100%",
      }}
    >
      <Grid
        container
        justifyContent="space-between"
        alignItems="flex-end"
        spacing={1}
      >
        <Grid item xs={12}>
          <div style={{ width: "100%" }}>
            <div style={{ display: "flex", height: "100%" }}>
              <div style={{ flexGrow: 1 }}>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  getRowId={(row) => row.id}
                  initialState={{
                    sorting: {
                      sortModel: [{ field: "name", sort: "asc" }],
                    },
                  }}
                  pageSize={pageSize}
                  onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  autoHeight
                  pagination
                  disableSelectionOnClick
                  checkboxSelection={false}
                  experimentalFeatures={{ newEditingApi: true }}
                  localeText={dataGridRoTextConstants}
                  components={{ Toolbar: CustomToolbar }}
                  componentsProps={{
                    pagination: {
                      labelRowsPerPage: "Număr de înregistrări pe pagină",
                    },
                    panel: {
                      anchorEl: filterButtonEl,
                      placement: "bottom-end",
                    },
                  }}
                  sx={{
                    "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus-within":
                      {
                        outline: "none",
                      },

                    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus":
                      {
                        outline: "none",
                      },
                  }}
                />
              </div>
            </div>
          </div>
        </Grid>
      </Grid>
      {openAddCityDialog && (
        <AddCityDialog
          open={openAddCityDialog}
          onCityCreated={fetchCities}
          onClose={() => {
            setOpenAddCityDialog(false);
          }}
          selectedCountyId={Number(id)}
        />
      )}
      {openEditCityDialog && (
        <EditCityDialog
          open={openEditCityDialog}
          city={selectedCityForEdit}
          onCityEdited={fetchCities}
          onClose={() => {
            setOpenEditCityDialog(false);
            setSelectedCityForEdit(undefined);
          }}
          selectedCountyId={Number(id)}
        />
      )}
      {openDeleteRowDialog && (
        <DeleteRowDialog
          open={openDeleteRowDialog}
          onClose={handleCloseDeleteRowDialog}
          onDelete={() => {
            if (toBeDeleted) {
              countryRequest
                .deleteCity(toBeDeleted)
                .then(fetchCities)
                .catch(() =>
                  alert(
                    "S-a produs o eroare! Este posibil ca înregistrarea aleasă să fie asociată cu alte tipuri de înregistrări și nu este permisă ștergerea!"
                  )
                );
            }
          }}
        />
      )}
    </Box>
  );
}
